import { SaveOutlined, SettingOutlined } from '@ant-design/icons';
import { ScopeButton, ToggleButton } from '@monorepo/shared-lib';
import { Divider, Input, Modal, Space, Typography } from 'antd';
import { FilterSelect } from 'components/Custom/selects';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { ConfirmBoxContainer } from 'containers/ConfirmBox';
import { MessageStates } from 'containers/MessageBox';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { DateOptions } from 'store/api/enums';
import {
  turnDrawerOff,
  turnDrawerOn,
  turnMessageOn,
  turnModalOn,
} from 'store/app/actions';
import { DataLogStatus, DrawerType, WindowType } from 'store/app/enums';
import { DrawerData, MessageData, ModalData } from 'store/app/types';
import { User } from 'store/auth/models';
import styled from 'styled-components';
import { Preference, PreferenceActions } from 'utils/preferences';
import { FilterViewsDrawer } from './FilterViewsDrawer';
import { SaveViewModalContent } from './SaveViewModalContent';

const ViewManagerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ViewsLabel = styled(Typography.Text)`
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  text-align: left;
  color: var(--color-text-dark, #042a2a);
  min-width: 35px;
`;

const StyledFilterSelect = styled(FilterSelect)<{ $hasValue?: boolean }>`
  min-width: 200px;

  ${(props) =>
    props.$hasValue &&
    `.ant-select-arrow {
      display: none !important;
    }`}

  /* Match filter styling - use same grey color as filters for selected value */
  .ant-select-selection-item {
    color: var(--color-text-muted, #5a6a6a) !important;
    font-size: 12px;
    font-weight: 600;
    line-height: 12px;
  }

  .ant-select-selection-placeholder {
    color: var(--color-placeholder, #9ca7b0) !important;
  }

  /* Green border when a view is selected (same as filters) */
  ${(props) =>
    props.$hasValue &&
    `.ant-select-selector {
      border: 2px solid var(--color-primary, #41aaaf) !important;
    }`}

  html[data-theme='dark'] & .ant-select-selection-item,
  html[data-theme='dark'] & .ant-select-selection-placeholder,
  html[data-theme='dark'] & .ant-select-arrow {
    color: var(--color-text-base, #e3eaeb) !important;
    opacity: 1 !important;
  }

  // Color only for clear — Ant controls opacity so × doesn't stack on the arrow.
  html[data-theme='dark'] & .ant-select-clear,
  html[data-theme='dark'] & .ant-select-clear .anticon,
  html[data-theme='dark'] & .ant-select-clear svg {
    color: var(--color-text-muted, #a9b8b9) !important;
  }

  html[data-theme='dark'] & .ant-select-clear svg path[stroke] {
    stroke: currentColor !important;
  }

  html[data-theme='dark'] & .ant-select-clear svg path[fill]:not([fill='none']) {
    fill: currentColor !important;
  }
`;

export interface FilterView {
  id: string;
  name: string;
  filters: any; // filtersState object
  createdAt: number;
  lastAccessed?: number;
  cachedData?: any[]; // Cached filtered data for instant switching
  cachedDataTimestamp?: number; // When the cache was created
  isStandard?: boolean; // If true, this is a standard view that cannot be deleted or modified
}

interface FilterViewManagerProps {
  currentFilters: any; // Current filtersState from ScopeTable
  onLoadView: (
    filters: any | null,
    isClearing?: boolean,
    viewId?: string,
    cachedData?: any[]
  ) => void; // Callback to apply filters, null to clear, isClearing flag, viewId and cachedData for instant loading
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
  currentUser?: User;
  onViewDataCached?: (viewId: string, data: any[]) => void; // Callback to cache filtered data for a view
  hasInitialFilter?: boolean; // If true, skip loading saved view (incoming filter request takes priority)
  initialViewId?: string; // View ID to load on mount (e.g., from query parameter)
  selectedViewId?: string | null; // External control of selected view ID (for preserving across view mode switches)
  onSelectedViewIdChange?: (viewId: string | null) => void; // Callback when selected view ID changes
  viewMode?: 'list' | 'board' | 'calendar'; // Current view mode to detect changes and re-apply selected view
  onClearViews?: (clearFn: () => void) => void; // Callback to receive the clearViews function
}

const FILTER_VIEWS_PREFERENCE = 'submissions-filter-views';
const LAST_ACCESSED_VIEW_PREFERENCE = 'submissions-last-accessed-view';

// Standard view IDs
const STANDARD_VIEW_MY_RETURNS = 'standard-view-my-returns';
const STANDARD_VIEW_DUE_THIS_MONTH = 'standard-view-due-this-month';

// Create standard views
const createStandardViews = (
  currentUser?: User,
  t?: (key: string) => string
): FilterView[] => {
  const standardViews: FilterView[] = [];

  if (currentUser) {
    // My Open Returns - returns where user is preparer, reviewer, or client (open returns only)
    // Use "user" filter which checks all 3 roles with OR logic
    // Exclude cancelled, submitted, and not started returns; include locked returns
    const userName =
      `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
      currentUser.email ||
      (t ? t('common.unknown') : 'Unknown');
    standardViews.push({
      id: STANDARD_VIEW_MY_RETURNS,
      name: t
        ? t('dashboard.card-title-my-open-returns')
        : 'My Open Returns',
      filters: {
        user: { value: [userName] }, // User filter that checks preparer, reviewer, and client with OR logic
        // Status filter to show open returns (exclude CANCELLED, SUBMITTED, NOT_STARTED; include LOCKED)
        // Use enum values (friendly names) to match the actual status values in the data
        status: {
          value: [
            DataLogStatus.LOCKED, // 'Locked'
            DataLogStatus.SAVED, // 'Saved'
            DataLogStatus.AWAITING_APPROVAL, // 'Awaiting Approval'
            DataLogStatus.INTERNAL_APPROVED, // 'Internal Approved'
            DataLogStatus.APPROVED, // 'Approved'
            DataLogStatus.AWAITING_CLIENT_APPROVAL, // 'Awaiting Client'
            DataLogStatus.CLIENT_APPROVED, // 'Client Approved'
          ],
        },
      },
      createdAt: 0,
      isStandard: true,
    });

    // Due this month - returns due in the current month
    standardViews.push({
      id: STANDARD_VIEW_DUE_THIS_MONTH,
      name: t
        ? t('submissions.standard-view-due-this-month')
        : 'Due this month',
      filters: {
        nextDueDate: { value: DateOptions.THIS_MONTH }, // Display "Due Date = This month" in filter UI
      },
      createdAt: 0,
      isStandard: true,
    });
  }

  return standardViews;
};

export const FilterViewManager: React.FC<FilterViewManagerProps> = ({
  currentFilters,
  onLoadView,
  handlePreferences,
  currentUser,
  hasInitialFilter,
  initialViewId,
  selectedViewId: externalSelectedViewId,
  onSelectedViewIdChange,
  viewMode,
  onClearViews,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [views, setViews] = useState<FilterView[]>([]);
  const [internalSelectedViewId, setInternalSelectedViewId] = useState<
    string | null
  >(null);

  // Use external selectedViewId if provided, otherwise use internal state
  const selectedViewId =
    externalSelectedViewId !== undefined
      ? externalSelectedViewId
      : internalSelectedViewId;
  const setSelectedViewId = (viewId: string | null) => {
    if (onSelectedViewIdChange) {
      onSelectedViewIdChange(viewId);
    }
    if (externalSelectedViewId === undefined) {
      setInternalSelectedViewId(viewId);
    }
  };
  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const [editingViewName, setEditingViewName] = useState('');
  const hasLoadedInitialView = useRef(false);
  const prevViewModeRef = useRef<'list' | 'board' | 'calendar' | undefined>(
    undefined
  );
  const loadViewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load saved views from preferences and merge with standard views
  useEffect(() => {
    if (currentUser && !hasLoadedInitialView.current) {
      const savedViews =
        handlePreferences(PreferenceActions.GET, {
          name: FILTER_VIEWS_PREFERENCE,
        }) || [];

      // Create standard views
      const standardViews = createStandardViews(currentUser, t);

      // Filter out any views that match standard view IDs or names (cleanup incorrectly saved standard views)
      const cleanedSavedViews = Array.isArray(savedViews)
        ? savedViews.filter((v: FilterView) => {
            // Remove views that match standard view IDs
            if (
              v.id === STANDARD_VIEW_MY_RETURNS ||
              v.id === STANDARD_VIEW_DUE_THIS_MONTH
            ) {
              return false;
            }
            // Remove views that match standard view names (case-insensitive)
            const standardViewNames = standardViews.map((sv) =>
              sv.name.toLowerCase()
            );
            if (standardViewNames.includes(v.name?.toLowerCase())) {
              return false;
            }
            // Remove views marked as standard (shouldn't be in saved views)
            return !v.isStandard;
          })
        : [];

      // If we cleaned up any views, save the cleaned list back to preferences
      if (
        cleanedSavedViews.length !==
        (Array.isArray(savedViews) ? savedViews.length : 0)
      ) {
        if (currentUser) {
          handlePreferences(PreferenceActions.SET, {
            name: FILTER_VIEWS_PREFERENCE,
            value: cleanedSavedViews,
          });
        }
      }

      // Merge: standard views first, then cleaned saved views
      const allViews = [...standardViews, ...cleanedSavedViews];

      setViews(allViews);

      // Priority order: initialViewId (from query param) > hasInitialFilter > lastAccessedView
      // Only load a view if there's no incoming filter request (unless it's a view request)
      if (initialViewId) {
        // Load the specific view requested (e.g., from dashboard card)
        const requestedView = allViews.find(
          (v: FilterView) => v.id === initialViewId
        );
        if (requestedView) {
          setSelectedViewId(initialViewId);
          hasLoadedInitialView.current = true;
          // Save as last accessed view
          handlePreferences(PreferenceActions.SET, {
            name: LAST_ACCESSED_VIEW_PREFERENCE,
            value: initialViewId,
          });
          // Load the filters after a short delay to ensure ScopeTable is ready
          // Pass viewId and cachedData for proper loading
          if (loadViewTimeoutRef.current)
            clearTimeout(loadViewTimeoutRef.current);
          loadViewTimeoutRef.current = setTimeout(() => {
            loadViewTimeoutRef.current = null;
            const cachedData = requestedView.cachedData;
            onLoadView(requestedView.filters, false, initialViewId, cachedData);
          }, 100);
        } else {
          // View not found, clear selection
          setSelectedViewId(null);
          hasLoadedInitialView.current = true;
        }
      } else if (!hasInitialFilter) {
        // No specific view requested and no initial filter - load last accessed view
        const lastAccessedId = handlePreferences(PreferenceActions.GET, {
          name: LAST_ACCESSED_VIEW_PREFERENCE,
        });

        if (lastAccessedId && typeof lastAccessedId === 'string') {
          const lastView = allViews.find(
            (v: FilterView) => v.id === lastAccessedId
          );
          if (lastView) {
            setSelectedViewId(lastAccessedId);
            hasLoadedInitialView.current = true;
            // Load the filters after a short delay to ensure ScopeTable is ready
            // Pass viewId and cachedData for proper loading
            if (loadViewTimeoutRef.current)
              clearTimeout(loadViewTimeoutRef.current);
            loadViewTimeoutRef.current = setTimeout(() => {
              loadViewTimeoutRef.current = null;
              const cachedData = lastView.cachedData;
              onLoadView(lastView.filters, false, lastAccessedId, cachedData);
            }, 100);
          }
        }
      } else {
        // Clear selected view when initialFilter is present (but no specific view requested)
        setSelectedViewId(null);
        hasLoadedInitialView.current = true;
      }
    }
    return () => {
      if (loadViewTimeoutRef.current) {
        clearTimeout(loadViewTimeoutRef.current);
        loadViewTimeoutRef.current = null;
      }
    };
  }, [
    currentUser,
    handlePreferences,
    hasInitialFilter,
    initialViewId,
    onLoadView,
  ]);

  // Re-apply selected view when viewMode changes
  useEffect(() => {
    if (viewMode === undefined) return;

    // Skip on initial mount
    if (prevViewModeRef.current === undefined) {
      prevViewModeRef.current = viewMode;
      return;
    }

    // Only re-apply view when viewMode actually changes and we have a selected view
    if (
      prevViewModeRef.current !== viewMode &&
      selectedViewId &&
      views.length > 0
    ) {
      const view = views.find((v) => v.id === selectedViewId);
      if (view) {
        // Re-apply the view's filters to the new view mode
        // Use cached data if available for instant loading
        const cachedData = view.cachedData;
        // Don't clear first - directly apply with cached data for instant switch
        onLoadView(view.filters, false, selectedViewId, cachedData);
      }
      prevViewModeRef.current = viewMode;
    } else if (prevViewModeRef.current !== viewMode) {
      prevViewModeRef.current = viewMode;
    }
  }, [viewMode, selectedViewId, views, onLoadView]);

  const saveViews = (updatedViews: FilterView[]) => {
    if (currentUser) {
      // Only save non-standard views to preferences
      const viewsToSave = updatedViews.filter((v) => !v.isStandard);
      handlePreferences(PreferenceActions.SET, {
        name: FILTER_VIEWS_PREFERENCE,
        value: viewsToSave,
      });
    }
  };

  // Function to clear selected view (but keep saved views in preferences)
  const clearAllViews = React.useCallback(() => {
    // Only clear the selected view, don't delete saved views from preferences
    setSelectedViewId(null);
    onSelectedViewIdChange && onSelectedViewIdChange(null);
  }, [onSelectedViewIdChange]);

  // Expose clearAllViews via onClearViews callback
  React.useEffect(() => {
    if (onClearViews) {
      onClearViews(clearAllViews);
    }
  }, [onClearViews, clearAllViews]);

  // Clear selected view and reset all filters (used when clicking cross or Clear in dropdown)
  const clearViewAndFilters = React.useCallback(() => {
    setSelectedViewId(null);
    onLoadView(null, false);
    setViewDropdownOpen(false);
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: LAST_ACCESSED_VIEW_PREFERENCE,
        value: '',
      });
    }
  }, [onLoadView, currentUser, handlePreferences]);

  const getViewLabel = (view: FilterView) => {
    if (view.id === STANDARD_VIEW_MY_RETURNS) {
      return t('dashboard.card-title-my-open-returns');
    }
    if (view.id === STANDARD_VIEW_DUE_THIS_MONTH) {
      return t('submissions.standard-view-due-this-month');
    }
    return view.name;
  };

  const handleSaveViewWithName = (viewName: string) => {
    if (!viewName.trim()) {
      const message: MessageData = {
        title: t('common.error'),
        description: t('submissions.filter-view-name-required'),
        type: MessageStates.ERROR,
      };
      dispatch(turnMessageOn(message));
      return;
    }

    // Limit to 6 non-standard views
    const nonStandardViews = views.filter((v) => !v.isStandard);
    if (nonStandardViews.length >= 6) {
      const message: MessageData = {
        title: t('common.error'),
        description: t('submissions.filter-views-max-reached'),
        type: MessageStates.ERROR,
      };
      dispatch(turnMessageOn(message));
      return;
    }

    const newView: FilterView = {
      id: `view-${Date.now()}`,
      name: viewName.trim(),
      filters: currentFilters,
      createdAt: Date.now(),
      lastAccessed: Date.now(),
    };

    const updatedViews = [...views, newView];
    setViews(updatedViews);
    saveViews(updatedViews);
    setSelectedViewId(newView.id);

    // Save as last accessed
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: LAST_ACCESSED_VIEW_PREFERENCE,
        value: newView.id,
      });
    }

    const message: MessageData = {
      title: t('common.success-exclamation'),
      description: t('submissions.filter-view-saved', {
        viewName: newView.name,
      }),
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));
  };

  const handleSaveAsExistingView = (viewId: string) => {
    // Prevent updating standard views
    const viewToUpdate = views.find((v) => v.id === viewId);
    if (viewToUpdate?.isStandard) {
      const message: MessageData = {
        title: t('common.error'),
        description: t('submissions.standard-views-cannot-update'),
        type: MessageStates.ERROR,
      };
      dispatch(turnMessageOn(message));
      return;
    }

    const updatedViews = views.map((view) =>
      view.id === viewId
        ? {
            ...view,
            filters: currentFilters,
            lastAccessed: Date.now(),
          }
        : view
    );
    setViews(updatedViews);
    saveViews(updatedViews);
    setSelectedViewId(viewId);

    // Save as last accessed
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: LAST_ACCESSED_VIEW_PREFERENCE,
        value: viewId,
      });
    }

    const viewName = views.find((v) => v.id === viewId)?.name || 'view';
    const message: MessageData = {
      title: t('common.success-exclamation'),
      description: t('submissions.filter-view-updated', { viewName }),
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));
  };

  const handleLoadView = (viewId: string) => {
    const view = views.find((v) => v.id === viewId);
    if (view) {
      setSelectedViewId(viewId);
      // Check if we have cached data for instant loading
      const cachedData = view.cachedData;
      // Apply new filters directly with loading - don't clear first to avoid double skeleton
      // Pass viewId and cachedData for instant data display
      onLoadView(view.filters, false, viewId, cachedData);

      // Update last accessed
      const updatedViews = views.map((v) =>
        v.id === viewId ? { ...v, lastAccessed: Date.now() } : v
      );
      setViews(updatedViews);
      saveViews(updatedViews);

      // Save as last accessed
      if (currentUser) {
        handlePreferences(PreferenceActions.SET, {
          name: LAST_ACCESSED_VIEW_PREFERENCE,
          value: viewId,
        });
      }
    }
  };

  const handleRenameView = (viewId: string) => {
    if (!editingViewName.trim()) {
      const message: MessageData = {
        title: t('common.error'),
        description: t('submissions.filter-view-name-required'),
        type: MessageStates.ERROR,
      };
      dispatch(turnMessageOn(message));
      return;
    }

    const updatedViews = views.map((view) =>
      view.id === viewId ? { ...view, name: editingViewName.trim() } : view
    );
    setViews(updatedViews);
    saveViews(updatedViews);
    setEditingViewId(null);
    setEditingViewName('');

    const message: MessageData = {
      title: t('common.success-exclamation'),
      description: t('submissions.filter-view-renamed'),
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));
  };

  const handleOpenEditViewsDrawer = () => {
    const drawerData: DrawerData = {
      title: t('submissions.edit-filter-views-title'),
      subtitle: t('submissions.edit-filter-views-subtitle'),
      type: DrawerType.FILTER_VIEWS_SETTINGS,
      component: (
        <FilterViewsDrawer
          views={views}
          onUpdate={(updatedViews) => {
            // Merge standard views back in (they should already be there, but ensure they are)
            const standardViews = createStandardViews(currentUser, t);
            const allViews = [
              ...standardViews,
              ...updatedViews.filter((v) => !v.isStandard),
            ];
            setViews(allViews);
            saveViews(allViews);
            // Clear selected view if it was deleted or if all views were deleted (excluding standards)
            const nonStandardViews = updatedViews.filter((v) => !v.isStandard);
            if (
              selectedViewId &&
              !allViews.find((v) => v.id === selectedViewId)
            ) {
              setSelectedViewId(null);
              // Clear filters if the selected view was deleted
              onLoadView(null, false);
              if (currentUser) {
                handlePreferences(PreferenceActions.SET, {
                  name: LAST_ACCESSED_VIEW_PREFERENCE,
                  value: '',
                });
              }
            }
            // If all non-standard views were deleted, also clear the selected view (only if it's not a standard view)
            if (
              nonStandardViews.length === 0 &&
              selectedViewId &&
              !standardViews.find((v) => v.id === selectedViewId)
            ) {
              setSelectedViewId(null);
              onLoadView(null, false);
              if (currentUser) {
                handlePreferences(PreferenceActions.SET, {
                  name: LAST_ACCESSED_VIEW_PREFERENCE,
                  value: '',
                });
              }
            }
          }}
          onClose={() => dispatch(turnDrawerOff())}
          handlePreferences={handlePreferences}
          currentUser={currentUser}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  // Check if current filters match the selected view
  const hasUnsavedChanges = useMemo(() => {
    if (!selectedViewId) return false;
    const selectedView = views.find((v) => v.id === selectedViewId);
    if (!selectedView) return false;
    return (
      JSON.stringify(selectedView.filters) !== JSON.stringify(currentFilters)
    );
  }, [selectedViewId, views, currentFilters]);

  const viewOptions = views.map((view) => ({
    label: getViewLabel(view),
    value: view.id,
  }));

  const selectContainerRef = useRef<HTMLDivElement>(null);
  const clearIconRef = useRef<HTMLElement | null>(null);
  const [clearIconPosition, setClearIconPosition] = useState<{
    top: number;
    right: number;
    width: number;
    height: number;
  } | null>(null);
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);

  useEffect(() => {
    type ClearIconPosition = {
      top: number;
      right: number;
      width: number;
      height: number;
    };

    const isSameClearIconPosition = (
      prev: ClearIconPosition | null,
      next: ClearIconPosition | null
    ) => {
      if (prev === next) return true;
      if (!prev || !next) return false;
      return (
        prev.top === next.top &&
        prev.right === next.right &&
        prev.width === next.width &&
        prev.height === next.height
      );
    };

    // Find clear icon and get its position for tooltip
    const updateClearIcon = () => {
      if (selectContainerRef.current && selectedViewId) {
        const clearIcon = selectContainerRef.current.querySelector(
          '.ant-select-clear'
        ) as HTMLElement;
        if (clearIcon) {
          clearIconRef.current = clearIcon;
          const rect = clearIcon.getBoundingClientRect();
          const containerRect =
            selectContainerRef.current.getBoundingClientRect();
          if (rect && containerRect) {
            const nextPosition: ClearIconPosition = {
              top: rect.top - containerRect.top,
              right: containerRect.right - rect.right,
              width: rect.width,
              height: rect.height,
            };
            setClearIconPosition((prev) =>
              isSameClearIconPosition(prev, nextPosition) ? prev : nextPosition
            );
          }
        } else {
          clearIconRef.current = null;
          setClearIconPosition((prev) => (prev === null ? prev : null));
        }
      } else {
        clearIconRef.current = null;
        setClearIconPosition((prev) => (prev === null ? prev : null));
      }
    };

    const timer = setTimeout(updateClearIcon, 100);

    const observer = new MutationObserver(updateClearIcon);
    if (selectContainerRef.current) {
      observer.observe(selectContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    // Also update on window resize
    window.addEventListener('resize', updateClearIcon);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('resize', updateClearIcon);
    };
  }, [selectedViewId]);

  return (
    <>
      <style>{`
        .filter-view-dropdown .ant-select-item {
          font-size: 12px !important;
          font-weight: 600 !important;
          line-height: 12px !important;
          padding: 4px 12px !important;
          min-height: 32px !important;
          display: flex !important;
          align-items: center !important;
        }
        .filter-view-dropdown .ant-select-item-option-content {
          font-size: 12px !important;
          font-weight: 600 !important;
          line-height: 12px !important;
        }
        .filter-view-dropdown .ant-select-item:hover {
          background-color: #f5f5f5 !important;
        }
        .filter-view-dropdown .ant-select-item-option-active {
          background-color: #f5f5f5 !important;
        }
        .filter-view-dropdown .ant-select-item-option-selected {
          background-color: transparent !important;
          font-weight: 600 !important;
        }
        .filter-view-dropdown .ant-select-item-option-selected:hover {
          background-color: #f5f5f5 !important;
        }
      `}</style>
      <ViewManagerContainer>
        <ViewsLabel>{t('submissions.views')}</ViewsLabel>
        <div
          ref={selectContainerRef}
          style={{ display: 'inline-block', position: 'relative' }}
        >
          <StyledFilterSelect
            $hasValue={!!selectedViewId}
            placeholder={t('submissions.select-filter-view')}
            aria-label={t('submissions.select-filter-view')}
            value={selectedViewId}
            open={viewDropdownOpen}
            onOpenChange={setViewDropdownOpen}
            onChange={(value) => {
              if (value) {
                handleLoadView(value as string);
              } else {
                clearViewAndFilters();
              }
            }}
            allowClear
            options={viewOptions}
            classNames={{
              popup: {
                root: 'filter-view-dropdown',
              },
            }}
            popupRender={(items) => {
              return (
                <Space
                  direction="vertical"
                  style={{ width: '100%', paddingBottom: '10px' }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {items}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ float: 'inline-end', margin: '0 10px' }}>
                    <ScopeButton
                      width="75px"
                      height="25px"
                      fontSize="12px"
                      lineheight="12px"
                      style={{ float: 'right' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearViewAndFilters();
                      }}
                      type="default"
                    >
                      {t('common.clear')}
                    </ScopeButton>
                  </Space>
                </Space>
              );
            }}
          />
          {selectedViewId && clearIconPosition && (
            <StyledTooltip
              title={t('submissions.clear-view-tooltip')}
              placement="top"
            >
              <span
                role="button"
                tabIndex={0}
                aria-label={t('submissions.clear-view-tooltip')}
                style={{
                  position: 'absolute',
                  right: `${clearIconPosition.right}px`,
                  top: `${clearIconPosition.top}px`,
                  width: `${clearIconPosition.width}px`,
                  height: `${clearIconPosition.height}px`,
                  pointerEvents: 'auto',
                  zIndex: 1000,
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  clearViewAndFilters();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    clearViewAndFilters();
                  }
                }}
              />
            </StyledTooltip>
          )}
        </div>
        <ToggleButton
          type="primary"
          shape="circle"
          icon={<SaveOutlined />}
          paddinglft="95px"
          aria-label={t('submissions.save-view')}
          onClick={() => {
            // Check if selected view is a standard view - standard views cannot be updated
            const selectedView = selectedViewId
              ? views.find((v) => v.id === selectedViewId)
              : null;
            const isStandardView = selectedView?.isStandard;

            // If a standard view is selected, only allow creating a new view (never update)
            if (isStandardView) {
              // Check if we've reached the maximum number of views before opening the create modal
              const nonStandardViews = views.filter((v) => !v.isStandard);
              if (nonStandardViews.length >= 6) {
                const message: MessageData = {
                  title: t('common.error'),
                  description: t('submissions.filter-views-max-reached'),
                  type: MessageStates.ERROR,
                };
                dispatch(turnMessageOn(message));
                return;
              }
              // Open modal using turnModalOn with ConfirmBoxContainer style
              const modalData: ModalData = {
                type: WindowType.WARNING,
                title: t('submissions.save-filter-view'),
                component: (
                  <SaveViewModalContent
                    onSave={(name) => handleSaveViewWithName(name)}
                    placeholder={t('submissions.filter-view-name-placeholder')}
                  />
                ),
              };
              dispatch(turnModalOn(modalData));
              return;
            }

            // If a view is selected and has unsaved changes, offer to update it
            if (selectedViewId && hasUnsavedChanges) {
              const modalData: ModalData = {
                type: WindowType.WARNING,
                title: t('submissions.update-existing-view-title'),
                component: (
                  <ConfirmBoxContainer
                    callback={() => handleSaveAsExistingView(selectedViewId)}
                    callback2={() => {
                      // Check if we've reached the maximum number of views before creating a new one
                      const nonStandardViews = views.filter(
                        (v) => !v.isStandard
                      );
                      if (nonStandardViews.length >= 6) {
                        const message: MessageData = {
                          title: t('common.error'),
                          description: t('submissions.filter-views-max-reached'),
                          type: MessageStates.ERROR,
                        };
                        dispatch(turnMessageOn(message));
                        return;
                      }
                      // Open modal using turnModalOn with ConfirmBoxContainer style
                      const modalData: ModalData = {
                        type: WindowType.WARNING,
                        title: t('submissions.save-filter-view'),
                        component: (
                          <SaveViewModalContent
                            onSave={(name) => handleSaveViewWithName(name)}
                            placeholder={t(
                              'submissions.filter-view-name-placeholder'
                            )}
                          />
                        ),
                      };
                      dispatch(turnModalOn(modalData));
                    }}
                    description={t(
                      'submissions.update-existing-view-description',
                      { viewName: selectedView?.name }
                    )}
                    okText={t('common.update')}
                    okText2={t('submissions.create-new')}
                    cancelText={t('common.cancel')}
                    showCancelBtn={true}
                  />
                ),
              };
              dispatch(turnModalOn(modalData));
            } else {
              // Check if we've reached the maximum number of views before opening the create modal
              const nonStandardViews = views.filter((v) => !v.isStandard);
              if (nonStandardViews.length >= 6) {
                const message: MessageData = {
                  title: t('common.error'),
                  description: t('submissions.filter-views-max-reached'),
                  type: MessageStates.ERROR,
                };
                dispatch(turnMessageOn(message));
                return;
              }
              // Open modal using turnModalOn with ConfirmBoxContainer style
              const modalData: ModalData = {
                type: WindowType.WARNING,
                title: t('submissions.save-filter-view'),
                component: (
                  <SaveViewModalContent
                    onSave={(name) => handleSaveViewWithName(name)}
                    placeholder={t('submissions.filter-view-name-placeholder')}
                  />
                ),
              };
              dispatch(turnModalOn(modalData));
            }
          }}
        >
          <span className="show-on-hover">{t('submissions.save-view')}</span>
        </ToggleButton>
        <ToggleButton
          type="primary"
          shape="circle"
          icon={<SettingOutlined />}
          paddinglft="95px"
          aria-label={t('submissions.edit-views')}
          onClick={handleOpenEditViewsDrawer}
        >
          <span className="show-on-hover">{t('submissions.edit-views')}</span>
        </ToggleButton>

        {/* Rename View Modal */}
        <Modal
          title={t('submissions.rename-filter-view')}
          open={editingViewId !== null}
          onOk={() => editingViewId && handleRenameView(editingViewId)}
          onCancel={() => {
            setEditingViewId(null);
            setEditingViewName('');
          }}
          okText={t('common.save')}
          cancelText={t('common.cancel')}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <label htmlFor="filter-view-rename-input">
                {t('submissions.view-name-label')}
              </label>
              <Input
                id="filter-view-rename-input"
                value={editingViewName}
                onChange={(e) => setEditingViewName(e.target.value)}
                placeholder={t('submissions.filter-view-rename-placeholder')}
                aria-label={t('submissions.view-name-label')}
                onPressEnter={() =>
                  editingViewId && handleRenameView(editingViewId)
                }
                autoFocus
              />
            </div>
          </Space>
        </Modal>
      </ViewManagerContainer>
    </>
  );
};
